import { queryOptions, useMutation } from '@tanstack/react-query'

import type { TResponseError } from '@/core/types/axios'
import type { TMutation } from '@/core/types/react-query'
import { questionPoolQueryKeys } from '@/modules/question-pool/question-pool.constants'
import { questionPoolApiService } from '@/modules/question-pool/question-pool.service'
import type {
  TCreateQuestionPoolRequest,
  TDeleteQuestionPoolRequest,
  TDeleteQuestionPoolResult,
  TEditQuestionPoolRequest,
  TQuestionPoolDetail,
  TQuestionPoolQuestionListParams,
  TQuestionPoolListParams,
  TToggleQuestionPoolStatusRequest,
  TUpdateQuestionPoolQuestionsRequest,
  TUpdateQuestionPoolQuestionsResult,
} from '@/modules/question-pool/question-pool.types'

export const questionPoolListQueryOptions = (params: TQuestionPoolListParams) =>
  queryOptions({
    queryKey: questionPoolQueryKeys.list(params),
    queryFn: () => questionPoolApiService.list(params),
  })

export const questionPoolOptionsQueryOptions = () =>
  queryOptions({
    queryKey: questionPoolQueryKeys.options(),
    queryFn: questionPoolApiService.options,
  })

export const questionPoolDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: questionPoolQueryKeys.detail(id),
    queryFn: () => questionPoolApiService.detail(id),
  })

export const questionPoolQuestionsQueryOptions = (
  id: string,
  params: TQuestionPoolQuestionListParams
) =>
  queryOptions({
    queryKey: questionPoolQueryKeys.questions(id, params),
    queryFn: () =>
      questionPoolApiService.listQuestions({ ...params, questionPoolId: id }),
  })

export const questionPoolCandidatesQueryOptions = (
  id: string,
  params: TQuestionPoolQuestionListParams
) =>
  queryOptions({
    queryKey: questionPoolQueryKeys.candidates(id, params),
    queryFn: () =>
      questionPoolApiService.listQuestions({
        ...params,
        poolState: params.includeOtherPools ? undefined : 'unassigned',
        excludeQuestionPoolId: id,
      }),
  })

export function useCreateQuestionPool({
  onSuccess,
  onError,
}: TMutation<TQuestionPoolDetail, TResponseError, TCreateQuestionPoolRequest>) {
  return useMutation<
    TQuestionPoolDetail,
    TResponseError,
    TCreateQuestionPoolRequest
  >({
    mutationFn: questionPoolApiService.create,
    onSuccess,
    onError,
  })
}

export function useEditQuestionPool({
  onSuccess,
  onError,
}: TMutation<TQuestionPoolDetail, TResponseError, TEditQuestionPoolRequest>) {
  return useMutation<
    TQuestionPoolDetail,
    TResponseError,
    TEditQuestionPoolRequest
  >({
    mutationFn: questionPoolApiService.edit,
    onSuccess,
    onError,
  })
}

export function useToggleQuestionPoolStatus({
  onSuccess,
  onError,
}: TMutation<
  TQuestionPoolDetail,
  TResponseError,
  TToggleQuestionPoolStatusRequest
>) {
  return useMutation<
    TQuestionPoolDetail,
    TResponseError,
    TToggleQuestionPoolStatusRequest
  >({
    mutationFn: questionPoolApiService.toggleStatus,
    onSuccess,
    onError,
  })
}

export function useDeleteQuestionPool({
  onSuccess,
  onError,
}: TMutation<
  TDeleteQuestionPoolResult,
  TResponseError,
  TDeleteQuestionPoolRequest
>) {
  return useMutation<
    TDeleteQuestionPoolResult,
    TResponseError,
    TDeleteQuestionPoolRequest
  >({
    mutationFn: questionPoolApiService.delete,
    onSuccess,
    onError,
  })
}

export function useUpdateQuestionPoolQuestions({
  onSuccess,
  onError,
}: TMutation<
  TUpdateQuestionPoolQuestionsResult,
  TResponseError,
  TUpdateQuestionPoolQuestionsRequest
>) {
  return useMutation<
    TUpdateQuestionPoolQuestionsResult,
    TResponseError,
    TUpdateQuestionPoolQuestionsRequest
  >({
    mutationFn: questionPoolApiService.updateQuestions,
    onSuccess,
    onError,
  })
}
